import { useEffect, useRef, useState } from "react";
import API from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, Buildings, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Companies House lookup widget.
 *
 * Used inside `ClientDetailsDialog` when the client is flagged as a
 * Company. Lets the solicitor either:
 *   (a) type a company name → debounced typeahead list of matches; or
 *   (b) type a registration number → "Look up" button fetches the
 *       full profile in one call.
 *
 * On selection, calls back to the parent with
 *   { company_name, company_number, status, registered_office_address }
 * so the parent can fill its form state in one go.
 *
 * Props:
 *   companyNumber  — current value in the form (auto-triggers status)
 *   onSelect(profile)  — callback when a company is picked
 *   onClear()      — optional: called when the search box is cleared
 */
const STATUS_PILL = {
  active: "bg-emerald-50 text-emerald-700",
  dissolved: "bg-rose-50 text-rose-700",
  liquidation: "bg-rose-50 text-rose-700",
  "in-administration": "bg-amber-50 text-amber-700",
  "voluntary-arrangement": "bg-amber-50 text-amber-700",
  closed: "bg-slate-100 text-slate-600",
  converted: "bg-slate-100 text-slate-600",
};

const _fmtStatus = (s) =>
  !s ? "" : s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function CompaniesHouseLookup({ companyNumber, onSelect }) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifiedStatus, setVerifiedStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // 400ms debounce so each keystroke doesn't fire a request
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 400);
    return () => clearTimeout(id);
  }, [query]);

  // Live search whenever the debounced term changes (>= 2 chars)
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (debounced.length < 2) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const { data } = await API.get(`/companies-house/search`, {
          params: { q: debounced, items_per_page: 8 },
        });
        if (!cancelled) {
          setResults(data.items || []);
          setOpen(true);
        }
      } catch (err) {
        if (!cancelled) {
          setResults([]);
          toast.error(err.response?.data?.detail || "Companies House search failed");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [debounced]);

  // Close the dropdown on outside click
  useEffect(() => {
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Auto-verify status whenever the parent's companyNumber changes to
  // something that looks like a CH number (8 chars alphanumeric).
  useEffect(() => {
    let cancelled = false;
    const num = (companyNumber || "").trim().toUpperCase().replace(/\s+/g, "");
    if (!/^[A-Z0-9]{6,10}$/.test(num)) {
      setVerifiedStatus(null);
      return;
    }
    (async () => {
      try {
        const { data } = await API.get(`/companies-house/${encodeURIComponent(num)}`);
        if (!cancelled) {
          setVerifiedStatus({
            status: data.status,
            company_name: data.company_name,
            company_number: data.company_number,
          });
        }
      } catch {
        if (!cancelled) setVerifiedStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, [companyNumber]);

  const handleLookupNumber = async () => {
    const num = query.trim().toUpperCase().replace(/\s+/g, "");
    if (!num) return;
    setVerifying(true);
    try {
      const { data } = await API.get(`/companies-house/${encodeURIComponent(num)}`);
      onSelect?.(data);
      setQuery("");
      setResults([]);
      setOpen(false);
      toast.success(`Loaded ${data.company_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No company found with that number");
    } finally {
      setVerifying(false);
    }
  };

  const handlePick = (item) => {
    onSelect?.(item);
    setQuery("");
    setResults([]);
    setOpen(false);
    toast.success(`Loaded ${item.company_name}`);
  };

  return (
    <div className="space-y-1.5" ref={wrapRef} data-testid="ch-lookup-wrapper">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <Input
            data-testid="ch-search-input"
            placeholder="Search Companies House by name or number…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length && setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleLookupNumber();
              }
            }}
            className="pl-8 text-sm h-9"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleLookupNumber}
          disabled={verifying || !query.trim()}
          data-testid="ch-lookup-btn"
          className="h-9"
        >
          {verifying ? "Looking up…" : "Look up"}
        </Button>
      </div>

      {/* Typeahead results dropdown */}
      {open && (loading || results.length > 0) && (
        <div className="relative">
          <div
            className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
            data-testid="ch-results"
          >
            {loading && (
              <div className="px-3 py-2 text-xs text-slate-400">Searching Companies House…</div>
            )}
            {!loading && results.map((c) => (
              <button
                type="button"
                key={c.company_number}
                onClick={() => handlePick(c)}
                data-testid={`ch-result-${c.company_number}`}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-slate-900 truncate">{c.company_name}</p>
                    <p className="text-[10px] text-slate-500">
                      {c.company_number}{c.date_of_creation ? ` · Incorporated ${new Date(c.date_of_creation).toLocaleDateString("en-GB", { year: "numeric", month: "short" })}` : ""}
                    </p>
                    {c.address && (
                      <p className="text-[10px] text-slate-400 truncate mt-0.5">{c.address}</p>
                    )}
                  </div>
                  {c.status && (
                    <span
                      className={`shrink-0 inline-flex text-[9px] font-medium px-1.5 py-0.5 rounded-full ${STATUS_PILL[c.status] || "bg-slate-100 text-slate-600"}`}
                    >
                      {_fmtStatus(c.status)}
                    </span>
                  )}
                </div>
              </button>
            ))}
            {!loading && results.length === 0 && debounced.length >= 2 && (
              <div className="px-3 py-2 text-xs text-slate-400">No matches on Companies House</div>
            )}
          </div>
        </div>
      )}

      {/* Live status pill for the number currently on the form */}
      {verifiedStatus?.status && (
        <p
          data-testid="ch-verified-status"
          className={`text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${STATUS_PILL[verifiedStatus.status] || "bg-slate-100 text-slate-600"}`}
          title="Live status from Companies House"
        >
          {verifiedStatus.status === "active"
            ? <CheckCircle weight="fill" className="w-2.5 h-2.5" />
            : <Warning weight="fill" className="w-2.5 h-2.5" />}
          {_fmtStatus(verifiedStatus.status)}
          {verifiedStatus.company_name && verifiedStatus.company_name.toUpperCase() !== (verifiedStatus.lookup_target || "").toUpperCase() && (
            <span className="text-slate-500"> · {verifiedStatus.company_name}</span>
          )}
        </p>
      )}
      <p className="text-[10px] text-slate-400 inline-flex items-center gap-1">
        <Buildings weight="fill" className="w-2.5 h-2.5" />
        Live data from Companies House — free public register
      </p>
    </div>
  );
}
